import { apiClient } from '../services/api';

export interface BillboardControlState {
  forceBlank: boolean;
  rotationSeconds: number;
  speedLimitMph: number;
}

export interface BillboardHealthSnapshot {
  lastTelemetryAt: string | null;
  lastSpeedMph: number | null;
  lastReasons: string[];
}

export interface DeviceTelemetrySnapshot {
  deviceId: string;
  lastEventAt: string | null;
  lastSpeedMph: number | null;
  lastPeopleCount: number | null;
}

export interface KnownBillboardDevicesResponse {
  items: DeviceTelemetrySnapshot[];
}

interface DeviceEventPayload {
  type: string;
  timestamp: string;
  metadata: {
    speedMph?: number;
    count?: number;
  };
}

export const getBillboardControl = async (): Promise<BillboardControlState> => {
  const { data } = await apiClient.get<BillboardControlState>('/billboard/control');
  return data;
};

export const updateBillboardControl = async (
  partial: Partial<BillboardControlState>,
): Promise<BillboardControlState> => {
  const { data } = await apiClient.patch<BillboardControlState>('/billboard/control', partial);
  return data;
};

export const getBillboardHealth = async (): Promise<BillboardHealthSnapshot> => {
  const { data } = await apiClient.get<BillboardHealthSnapshot>('/billboard/health');
  return data;
};

export const getDeviceTelemetry = async (
  deviceId: string,
): Promise<DeviceTelemetrySnapshot> => {
  const { data } = await apiClient.get<DeviceTelemetrySnapshot>(`/devices/${encodeURIComponent(deviceId)}/telemetry`);
  return data;
};

export const getKnownBillboardDevices = async (): Promise<DeviceTelemetrySnapshot[]> => {
  const { data } = await apiClient.get<KnownBillboardDevicesResponse>('/devices');
  return Array.isArray(data.items) ? data.items : [];
};

export const registerBillboardDevice = async (
  deviceId: string,
): Promise<DeviceTelemetrySnapshot> => {
  const { data } = await apiClient.post<DeviceTelemetrySnapshot>(
    '/devices/register',
    { deviceId },
  );
  return data;
};

export const sendSimulatedDeviceSpeed = async (
  deviceId: string,
  speedMph: number,
): Promise<{ status: string }> => {
  const payload = {
    timestamp: new Date().toISOString(),
    events: [
      {
        type: 'telemetry',
        timestamp: new Date().toISOString(),
        metadata: {
          speedMph,
        },
      } satisfies DeviceEventPayload,
    ],
  };

  const { data } = await apiClient.post<{ status: string }>(
    `/devices/${encodeURIComponent(deviceId)}/events`,
    payload,
  );
  return data;
};

/* ------------------------------------------------------------------ */
/*  Audience demographics (privacy-safe: aggregate counts only)       */
/* ------------------------------------------------------------------ */

export interface AgeGroupCounts {
  '0-17': number;
  '18-24': number;
  '25-34': number;
  '35-44': number;
  '45-54': number;
  '55-64': number;
  '65+': number;
}

export interface GenderCounts {
  male: number;
  female: number;
  unknown: number;
}

export interface DemographicsResponse {
  totalPeople: number;
  ageGroups: AgeGroupCounts;
  gender: GenderCounts;
  dominantAgeRange: string;
  dominantGender: string;
  objectsDetected: Record<string, number>;
  hourly: Array<{
    bucket: string;
    totalPeople: number;
    ageGroups: Record<string, number>;
    gender: Record<string, number>;
  }>;
}

export const getBillboardDemographics = async (
  deviceId?: string,
  hours = 1,
  adId?: string,
): Promise<DemographicsResponse> => {
  const params: Record<string, string> = { hours: String(hours) };
  if (deviceId) params.deviceId = deviceId;
  if (adId) params.adId = adId;

  const { data } = await apiClient.get<DemographicsResponse>('/billboard/demographics', { params });
  return data;
};

/* ------------------------------------------------------------------ */
/*  Billboard launch / playlist API                                   */
/* ------------------------------------------------------------------ */

export interface BillboardAssignmentDTO {
  _id: string;
  adId: string;
  adTitle?: string;
  adCreativeType?: string;
  deviceId: string;
  status: 'active' | 'paused' | 'ended';
  weight: number;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  launchedAt: string;
}

export interface PlaylistItem {
  assignmentId: string;
  adId: string;
  title: string;
  description: string;
  creativeType?: string;
  imageUrl?: string;
  videoUrl?: string;
  landingUrl?: string;
  weight: number;
  deviceId: string;
  launchedAt: string;
}

export const launchAdToBillboard = async (
  adId: string,
  deviceId?: string,
  weight?: number,
  scheduledStart?: string,
  scheduledEnd?: string,
): Promise<{ message: string; assignment: BillboardAssignmentDTO }> => {
  const body: Record<string, unknown> = { adId };
  if (deviceId) body.deviceId = deviceId;
  if (weight) body.weight = weight;
  if (scheduledStart) body.scheduledStart = scheduledStart;
  if (scheduledEnd) body.scheduledEnd = scheduledEnd;

  const { data } = await apiClient.post<{ message: string; assignment: BillboardAssignmentDTO }>(
    '/billboard/launch',
    body,
  );
  return data;
};

export const updateBillboardAssignment = async (
  id: string,
  update: { status?: 'active' | 'paused' | 'ended'; weight?: number },
): Promise<BillboardAssignmentDTO> => {
  const { data } = await apiClient.patch<BillboardAssignmentDTO>(
    `/billboard/launch/${encodeURIComponent(id)}`,
    update,
  );
  return data;
};

export const removeBillboardAssignment = async (id: string): Promise<{ message: string }> => {
  const { data } = await apiClient.delete<{ message: string }>(
    `/billboard/launch/${encodeURIComponent(id)}`,
  );
  return data;
};

export const getBillboardPlaylist = async (
  deviceId?: string,
): Promise<{ items: PlaylistItem[]; total: number }> => {
  const params: Record<string, string> = {};
  if (deviceId) params.deviceId = deviceId;

  const { data } = await apiClient.get<{ items: PlaylistItem[]; total: number }>(
    '/billboard/playlist',
    { params },
  );
  return data;
};

export const listBillboardAssignments = async (): Promise<{ items: BillboardAssignmentDTO[] }> => {
  const { data } = await apiClient.get<{ items: BillboardAssignmentDTO[] }>(
    '/billboard/assignments',
  );
  return data;
};

/* ------------------------------------------------------------------ */
/*  Campaign status lifecycle                                         */
/* ------------------------------------------------------------------ */

export type CampaignStatus = 'draft' | 'review' | 'approved' | 'live' | 'ended';

export const updateCampaignStatus = async (
  adId: string,
  status: CampaignStatus,
): Promise<{ _id: string; campaignStatus: CampaignStatus }> => {
  const { data } = await apiClient.patch<{ _id: string; campaignStatus: CampaignStatus }>(
    `/ads/${encodeURIComponent(adId)}/campaign-status`,
    { status },
  );
  return data;
};
